// TRẠNG THÁI kịch bản (Nháp đỏ / Chạy thử vàng / Hoàn thiện xanh lá) + đổi tên tại chỗ + lọc + nhập tệp — dùng trên trang Kịch bản.
// Chấm màu LUÔN kèm chữ (title/aria-label + chữ hiện) — không chỉ dựa vào màu. Mọi chữ dựng bằng textContent.
import { api, errMsg } from "../api";
import { busy, clear, h, toast } from "../dom";
import { runStatusText, thayMaBuoc } from "../format";
import { loiDeHieu, tachLyDoLuot, type LoiDeHieu } from "../../../../src/shared/loi-de-hieu";
import { layTenBuoc } from "../ten-kb";
import { lamSachMa } from "../../../../src/shared/ten-ma-noi-bo";

export type Nhan = "nhap" | "chay_thu" | "hoan_thien";
export const NHAN: Nhan[] = ["nhap", "chay_thu", "hoan_thien"];
// (08/10/2026, người dùng bỏ Nháp) GIỮ mã lưu cũ (dữ liệu cũ nạp nguyên vẹn) — chỉ đổi CHỮ: nhap → "Mới", chay_thu → "Đang dùng", hoan_thien → "Hoàn thiện".
export const TEN_NHAN: Record<Nhan, string> = { nhap: "Mới", chay_thu: "Đang dùng", hoan_thien: "Hoàn thiện" };
const GIAI_THICH: Record<Nhan, string> = {
  nhap: "Mới — kịch bản vừa tạo, chưa chạy lần nào",
  chay_thu: "Đang dùng — đã chạy (bấm Chạy là tự sang trạng thái này)",
  hoan_thien: "Hoàn thiện — bạn đã chốt dùng được",
};
const laNhan = (v: unknown): v is Nhan => typeof v === "string" && (NHAN as string[]).includes(v);

/** Chấm màu + chữ "KỊCH BẢN (Nháp)". */
export function chamNhan(nhan: string, testid = "kb-nhan"): HTMLElement {
  const n: Nhan = laNhan(nhan) ? nhan : "nhap";
  return h("span", { class: `kb-nhan kb-nhan-${n}`, "data-testid": testid, dataset: { nhan: n }, title: GIAI_THICH[n], "aria-label": `Trạng thái: ${TEN_NHAN[n]}` },
    h("span", { class: "kb-cham", "aria-hidden": "true" }), `KỊCH BẢN (${TEN_NHAN[n]})`);
}

// ---- KẾT QUẢ CHẠY THỬ THẬT (rà soát 05/10/2026 N4): thay thẻ xanh "Sẵn sàng" (gắn cả kịch bản hỏng / chưa chạy thử lần nào) bằng đúng kết
// quả lần chạy thử gần nhất (máy chủ tính chung cho chạy một bot + chạy nhiều bot (cùng thẻ "Chạy kịch bản") — server/src/kich-ban.ts tomTatChay). ----
export interface TomTatChay { ketQua: "chua" | "dang" | "sach" | "loi" | "cu"; luc: string | null; botTen: string[]; lyDo: string | null; duocChayThat: boolean }
export const TEN_LOC_CHAY: Record<TomTatChay["ketQua"], string> = { chua: "Chưa chạy lần nào", dang: "Đang chạy", sach: "Lần chạy gần nhất đạt", loi: "Lần chạy gần nhất lỗi", cu: "Đã sửa sau lần chạy gần nhất" };
export const ngayThang = (iso: string | null): string => {
  const t = iso ? new Date(iso) : null;
  return t && !Number.isNaN(t.getTime()) ? `${String(t.getDate()).padStart(2, "0")}/${String(t.getMonth() + 1).padStart(2, "0")}` : "";
};
const tenBot = (ds: string[]): string => (ds.length <= 2 ? ds.join(", ") : `${ds.slice(0, 2).join(", ")} +${ds.length - 2} bot`);
const cat = (s: string, n: number): string => (Array.from(s).length > n ? `${Array.from(s).slice(0, n - 1).join("")}…` : s);
/** Lý do lỗi của lần chạy gần nhất do plugin sinh ("kết thúc: done ⚠ …" + các dòng nhật ký bước) → chữ người đọc: mã trạng thái → tiếng Việt
 *  ("done" → "xong"), mã bước → tên bước, chỉ giữ dòng tóm tắt đầu (các dòng sau là nhật ký từng bước, vẫn có trong tooltip nguyên văn). */
export function lyDoDeDoc(lyDo: string | null | undefined): string {
  const dong = (lyDo ?? "").split(/\r?\n/)[0] ?? "";
  const vi = dong.replace(/^(kết thúc: )([a-z_]+)/, (m, dau: string, tt: string) => { const t = runStatusText(tt); return t && t !== tt && /^[A-ZÀ-Ỹ]/.test(t) ? `${dau}${t.charAt(0).toLowerCase()}${t.slice(1)}` : m; });
  return lamSachMa(vi);
}
/** (A6, 08/10/2026) Lỗi của lần chạy gần nhất → câu DỄ HIỂU (loiDeHieu, src/shared/loi-de-hieu.ts), KHÔNG dán chữ thô của bot ("…chỉ lấy được 0 mục … (min_items) — tran…"). `buoc` = tên bước đã biết (tuỳ chọn). */
export function loiChayThuDeHieu(c: TomTatChay, buoc?: string | null): LoiDeHieu | null {
  if (c.ketQua !== "loi" || !c.lyDo) return null;
  return loiDeHieu(c.lyDo, { buoc: buoc ?? null });
}
/** Chữ kết quả: "Lần chạy gần nhất đạt 05/10 · Máy Một" / "Lần chạy gần nhất lỗi: <câu dễ hiểu>"… */
export function chuChayThu(c: TomTatChay, buoc?: string | null): string {
  const bot = tenBot(c.botTen);
  // (08/10/2026, người dùng bỏ Nháp) kết quả LẦN CHẠY GẦN NHẤT (chạy thật; lần chạy thử cũ vẫn tính) — không còn "chạy thử".
  if (c.ketQua === "sach") return `Lần chạy gần nhất đạt ${ngayThang(c.luc)}${bot ? ` · ${bot}` : ""}`;
  if (c.ketQua === "loi") { const l = loiChayThuDeHieu(c, buoc); return `Lần chạy gần nhất lỗi${l ? `: ${cat(l.cau, 160)}` : ""}`; }
  if (c.ketQua === "dang") return `Đang chạy${bot ? ` trên ${bot}` : ""}…`;
  if (c.ketQua === "cu") return "Đã sửa sau lần chạy gần nhất";
  return "Chưa chạy lần nào";
}
/** Thẻ kết quả (danh sách + đầu trang chi tiết). Lỗi: CHỈ câu dễ hiểu; "Cách sửa" ở title (và khối khoiLoiChayThu ở đầu trang); chữ thô KHÔNG hiện ở đây.
 *  `bundleName` (tuỳ chọn): hỏi gộp tên bước (như ganLyDo) để câu nói đúng bước thay vì "Một bước". */
export function theChayThu(c: TomTatChay | undefined, testid = "kb-chay-tt", bundleName?: string | null): HTMLElement {
  const k = c ?? { ketQua: "chua", luc: null, botTen: [], lyDo: null, duocChayThat: false };
  const lop = k.ketQua === "sach" ? "ok" : k.ketQua === "loi" ? "err" : k.ketQua === "cu" ? "warn" : k.ketQua === "dang" ? "running" : "";
  const tieuDe = (buoc?: string | null): string => { const l = loiChayThuDeHieu(k, buoc); return l ? `${chuChayThu(k, buoc)}\nCách sửa: ${l.cachSua}` : chuChayThu(k, buoc); };
  const el = h("span", { class: `tag kb-chay-tt ${lop}`, "data-testid": testid, dataset: { ketQua: k.ketQua }, title: tieuDe() }, chuChayThu(k));
  const goc = k.lyDo;
  if (k.ketQua === "loi" && goc && bundleName) {
    const maBuoc = tachLyDoLuot(goc).maBuoc;
    if (maBuoc) layTenBuoc(bundleName, (_tra, biet) => {
      const ten = biet(maBuoc);
      if (ten) { const b = ten.replace(/^Bước \d+ · /, ""); el.textContent = chuChayThu(k, b); el.title = tieuDe(b); }
    });
  }
  return el;
}
/** Khối ở ĐẦU TRANG chi tiết khi lần chạy gần nhất lỗi: "Cách sửa: …" + "Chi tiết kỹ thuật" (thu gọn — nơi DUY NHẤT hiện chữ thô). Không lỗi → null. */
export function khoiLoiChayThu(c: TomTatChay | undefined): HTMLElement | null {
  if (!c) return null;
  const l = loiChayThuDeHieu(c);
  if (!l) return null;
  return h("div", { class: "kb-chay-loi small", "data-testid": "kb-chay-loi" },
    h("p", { class: "kb-chay-cach-sua", "data-testid": "kb-chay-cach-sua" }, h("b", {}, "Cách sửa: "), l.cachSua),
    l.kyThuat ? h("details", { class: "kb-loi-ky-thuat", "data-testid": "kb-chay-ky-thuat" }, h("summary", {}, "Chi tiết kỹ thuật"), h("code", {}, l.kyThuat)) : null);
}

/** Ô chọn trạng thái trên trang chi tiết. N4 (05/10/2026): "Hoàn thiện" chỉ chọn được khi bản HIỆN TẠI đã chạy thử sạch (được chạy thật) —
 *  trước đây đặt tay được khi chưa chạy thử lần nào, nhãn xanh nói dối. Đang Hoàn thiện thì vẫn giữ (để chọn lại / hạ xuống). */
export function chonNhan(id: string, nhan: string, sauKhiDoi: (moi: Nhan) => void, hoanThienDuoc: { duoc: boolean; lyDo: string } = { duoc: true, lyDo: "" }): HTMLElement {
  void hoanThienDuoc; // (08/10/2026, bỏ Nháp) "Hoàn thiện" không còn đòi chạy thử sạch
  const khoaHt = false;
  const sel = h("select", { "data-testid": "kb-chon-nhan", title: khoaHt ? `Chưa đặt được “Hoàn thiện”: ${hoanThienDuoc.lyDo}` : "Đặt trạng thái kịch bản (chỉ là nhãn để bạn quản lý)", "aria-label": "Trạng thái kịch bản" },
    ...NHAN.map((n) => h("option", { value: n, selected: n === nhan, ...(n === "hoan_thien" && khoaHt ? { disabled: true, title: "" } : {}) }, n === "hoan_thien" && khoaHt ? TEN_NHAN[n] : TEN_NHAN[n])));
  let cu = nhan;
  sel.addEventListener("change", () => void (async () => {
    const moi = sel.value as Nhan;
    sel.disabled = true;
    try {
      const r = await api<{ nhan: Nhan }>("PATCH", `/api/kich-ban/${encodeURIComponent(id)}`, { nhan: moi });
      cu = r.nhan;
      toast(`Đã đặt trạng thái: ${TEN_NHAN[r.nhan]}`);
      sauKhiDoi(r.nhan);
    } catch (e) { sel.value = cu; toast(errMsg(e), "err"); }
    finally { sel.disabled = false; }
  })());
  return h("label", { class: "kb-chon-nhan" }, h("span", { class: "small muted" }, "Trạng thái"), sel);
}

/** Tên + nút "Đổi tên" → ô sửa tại chỗ (Enter lưu, Esc huỷ). */
export function tenSuaDuoc(d: { id: string; ten: string; phienBan: number }, sauKhiDoi: (r: { ten: string; thongBao: string | null }) => void): HTMLElement {
  const wrap = h("div", { class: "kb-ten-wrap" });
  const ve = (): void => {
    clear(wrap);
    const doi = h("button", { type: "button", class: "mini", "data-testid": "kb-doi-ten", title: "Đổi tên kịch bản — chỉ đổi tên hiển thị" }, "Đổi tên");
    doi.addEventListener("click", sua);
    wrap.append(h("h1", { class: "kb-ten", "data-testid": "kb-ten" }, d.ten), doi);
  };
  const sua = (): void => {
    clear(wrap);
    const o = h("input", { value: d.ten, maxlength: 400, "data-testid": "kb-ten-o", "aria-label": "Tên mới của kịch bản", title: "Tối đa 120 ký tự" });
    const loi = h("span", { class: "err small", "data-testid": "kb-ten-loi", hidden: true });
    const luu = h("button", { type: "button", class: "pri mini", "data-testid": "kb-ten-luu", title: "Lưu tên mới" }, "Lưu");
    const huy = h("button", { type: "button", class: "mini", "data-testid": "kb-ten-huy", title: "Giữ tên cũ" }, "Huỷ");
    const gui = () => void busy(luu, "…", async () => {
      loi.hidden = true;
      try {
        const r = await api<{ ten: string; thongBao: string | null; phienBan: number }>("PATCH", `/api/kich-ban/${encodeURIComponent(d.id)}`, { ten: o.value, phienBan: d.phienBan });
        d.ten = r.ten; d.phienBan = r.phienBan;
        toast("Đã đổi tên");
        sauKhiDoi(r);
      } catch (e) { loi.textContent = errMsg(e); loi.hidden = false; o.focus(); }
    });
    luu.addEventListener("click", gui);
    huy.addEventListener("click", ve);
    o.addEventListener("keydown", (e) => { if (e.key === "Enter") { e.preventDefault(); gui(); } else if (e.key === "Escape") ve(); });
    wrap.append(h("div", { class: "kb-ten-sua" }, o, luu, huy), loi);
    o.focus();
    o.select();
  };
  ve();
  return wrap;
}

/** Bộ lọc danh sách theo trạng thái. */
export function locNhan(hienTai: string, doi: (n: string) => void): HTMLElement {
  const sel = h("select", { "data-testid": "kb-loc-nhan", title: "Chỉ hiện kịch bản ở trạng thái này", "aria-label": "Lọc theo trạng thái" },
    h("option", { value: "", selected: !hienTai }, "Tất cả"), ...NHAN.map((n) => h("option", { value: n, selected: n === hienTai }, TEN_NHAN[n])));
  sel.addEventListener("change", () => doi(sel.value));
  return h("label", { class: "kb-loc" }, h("span", { class: "small muted" }, "Trạng thái"), sel);
}

/** Thẻ "Nhập tệp kịch bản" (tệp .json do nút Xuất tạo) + chọn trạng thái khi nhập. */
export function theNhapTep(sauKhiNhap: (id: string) => void): HTMLElement {
  const tep = h("input", { type: "file", accept: ".json,application/json", "data-testid": "kb-nhap-tep", title: "Chọn tệp .json do nút Xuất của trang Kịch bản tạo ra" });
  const nhan = h("select", { "data-testid": "kb-nhap-nhan", title: "Trạng thái của kịch bản sau khi nhập" },
    h("option", { value: "" }, "Theo tệp (tệp không ghi → Mới)"), ...NHAN.map((n) => h("option", { value: n }, TEN_NHAN[n])));
  const nut = h("button", { type: "button", "data-testid": "kb-nhap-nut", title: "Nhập kịch bản từ tệp thành một kịch bản mới của bạn" }, "Nhập");
  const loi = h("div", { class: "err-box", hidden: true, "data-testid": "kb-nhap-loi" });
  nut.addEventListener("click", () => void busy(nut, "Đang nhập…", async () => {
    loi.hidden = true;
    const f = tep.files?.[0];
    if (!f) { loi.textContent = "Chọn tệp .json trước khi bấm Nhập"; loi.hidden = false; return; }
    if (f.size > 4 * 1024 * 1024) { loi.textContent = "Tệp quá lớn (tối đa 4 MB)"; loi.hidden = false; return; }
    try {
      const r = await api<{ id: string; ten: string; nhan: Nhan }>("POST", "/api/kich-ban/nhap-tep", { tep: await f.text(), ...(nhan.value ? { nhan: nhan.value } : {}) });
      toast(`Đã nhập “${r.ten}” — trạng thái ${TEN_NHAN[r.nhan]}`);
      sauKhiNhap(r.id);
    } catch (e) { loi.textContent = errMsg(e); loi.hidden = false; }
  }));
  // (07/10/2026, người dùng: thẻ "lơ lửng") Thẻ GỌN như các thẻ khác: tiêu đề h3 (+ ⓘ do goi-y.ts gắn từ đoạn giải thích bên dưới), một hàng
  // Tệp · Trạng thái · Nhập — thấy ngay, không phải mở <details> như trước. Trang Kịch bản đặt thẻ này ngay dưới "Tạo kịch bản mới" (cột trái).
  return h("div", { class: "card kb-nhap", "data-testid": "kb-nhap-the" }, h("h3", {}, "Nhập tệp kịch bản"),
    h("p", { class: "small muted" }, "Tệp .json do nút Xuất tạo ra (của bạn hoặc người khác gửi). Kịch bản nhập vào là một kịch bản MỚI của bạn."),
    h("div", { class: "kb-nhap-hang" }, h("label", { class: "f kb-nhap-tep-o" }, h("span", {}, "Tệp"), tep), h("label", { class: "f" }, h("span", {}, "Trạng thái"), nhan), nut), loi);
}
